import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Typography, Box, useMediaQuery } from '@mui/material';
import moment from 'moment';

import apis from '@src/apis';
import {
  FREE_PACKAGE_CODES,
  PACKAGE_LEVEL,
  PACKAGE_STUDIO_VERSION,
  PACKAGE_TYPE,
} from '@src/constants/package';
import { PAYMENT_HUB_RENEWAL_TYPE } from '@src/constants/subscription';
import theme from '@src/styles/theme';
import useUserInfo from '@src/hooks/useTtsUser';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { StyledSubscriptionV3 } from './index.style';
import SubscriptionItemV2V3 from './SubscriptionItemV2V3';
import SubscriptionItemV3 from './SubscriptionItemV3';
import SubscriptionItemV2 from './SubscriptionItemV2';
import SubscriptionCustom from './SubscriptionCustomItem';
import SubscriptionCustomAndV2Item from './SubscriptionCustomAndV2Item';
import SubscriptionNew from './SubscriptionNew';

const Subscription = () => {
  const [usingOrders, setUsingOrders] = useState([]);
  const [lastUsingOrder, setLastUsingOrder] = useState({});

  const { getFeatureValue } = useFeatureFlags();

  const isMobile = useMediaQuery(theme.breakpoints.down('smd'));

  const { subscriptionPurchase, usingPackage } = useSelector(
    (state) => state.user,
  );
  const user = useSelector((state) => state.auth.user);
  const isNewUISubscriptionEnabled = getFeatureValue(
    FEATURE_KEYS.NEW_UI_MY_PACKAGE,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );
  const { user: userInfo } = useSelector((state) => state.auth);
  const { preservationExpiryDate, packageExpiryDate } = userInfo;
  const { ttsUser } = useUserInfo();
  const { studio } = ttsUser;
  const { cycle, topUp, custom, oneTime } = studio || {};

  const {
    capturedRolloverCredits: capturedRolloverCreditsCycle = 0,
    remainingCredits: remainingCycleCredits = 0,
    nextResetAt,
    purchasedTopUpCredits,
    capturedReferralCredits: capturedReferralCreditsCycle = 0,
    creditsPerCycle = 0,
    apiUsedCredits = 0,
  } = cycle || {};

  const {
    remainingCredits: remainingTopUpCredits = 0,
    capturedRolloverCredits: capturedRolloverCreditsTopUp = 0,
  } = topUp || {};

  const {
    remainingCredits: remainingCustomCredits = 0,
    capturedReferralCredits: capturedReferralCreditsCustom = 0,
  } = custom || {};

  const { capturedReferralCredits: captureReferralCreditsOneTime = 0 } =
    oneTime || {};

  const { t } = useTranslation();

  const totalRemainingCredits = remainingCycleCredits + remainingTopUpCredits;

  const totalRemainingApiCredits = apiUsedCredits;

  const totalCapturedRolloverCredits =
    capturedRolloverCreditsCycle + capturedRolloverCreditsTopUp;

  const handleSetUsingOrders = (currUsingOrders) => {
    // Check saveUsingOrder have studio order
    const hasStudioOrder = currUsingOrders.find((order) => {
      if (!order?.package) return false;

      const isStudioType = order.package.type === PACKAGE_TYPE.STUDIO;
      const isNotExpired = moment(packageExpiryDate).isAfter(moment());
      const isAutoRenewOrder =
        order?.renewalType === PAYMENT_HUB_RENEWAL_TYPE.AUTO_RENEW;

      return isStudioType && (isNotExpired || isAutoRenewOrder);
    });

    let currPackages = currUsingOrders;

    if (!hasStudioOrder) {
      const studioOrder = {
        package: usingPackage,
        packageExpiryDate: userInfo.packageExpiryDate,
        type: PACKAGE_TYPE.STUDIO,
      };
      currPackages = currUsingOrders.filter(
        (order) => order?.package?.type !== PACKAGE_TYPE.STUDIO,
      );

      // Add studio order to first of saveUsingOrders
      currPackages.unshift(studioOrder);
    }

    setUsingOrders(currPackages);
  };

  const fetchUsingOrders = async () => {
    const data = await apis.orders.getUsingOrders();
    if (data && data.status) {
      handleSetUsingOrders(data.result);
      setLastUsingOrder(data.result[0]);
    }
  };

  useEffect(() => {
    fetchUsingOrders();
  }, [usingPackage]);

  const renderSubscriptionItem = (usingOrder) => {
    const isPackageV3 =
      usingOrder?.type === PACKAGE_TYPE.STUDIO &&
      usingOrder?.package?.version?.includes(PACKAGE_STUDIO_VERSION.V3);

    const isCustomPackage =
      usingOrder?.type === PACKAGE_TYPE.STUDIO &&
      usingOrder?.package?.level === PACKAGE_LEVEL.CUSTOM;

    const hasV2Package = Boolean(userInfo?.remainingCharacters);
    const packageCode = usingOrder?.package?.code;
    const isFreePackage = FREE_PACKAGE_CODES.includes(packageCode);
    const isDubbingPackage = usingOrder?.type === PACKAGE_TYPE.DUBBING;
    const isApiPackage = usingOrder?.type === PACKAGE_TYPE.API;

    if (isCustomPackage && hasV2Package)
      return (
        <SubscriptionCustomAndV2Item
          packageInfo={usingOrder?.package}
          usingOrder={usingOrder}
          totalRemainingCredits={remainingCustomCredits}
          isMobile={isMobile}
          capturedReferralCreditsCustom={capturedReferralCreditsCustom}
        />
      );

    if (isCustomPackage && !isFreePackage)
      return (
        <SubscriptionCustom
          packageInfo={usingOrder?.package}
          usingOrder={usingOrder}
          hasV2Package={hasV2Package}
          totalRemainingCredits={remainingCustomCredits}
          isMobile={isMobile}
          lastUsingOrder={lastUsingOrder}
          capturedReferralCreditsCustom={capturedReferralCreditsCustom}
        />
      );

    if (isPackageV3 && hasV2Package) {
      return (
        <SubscriptionItemV2V3
          packageInfo={usingOrder?.package}
          usingOrder={usingOrder}
          hasV2Package={hasV2Package}
          totalRemainingCredits={totalRemainingCredits}
          nextResetAt={nextResetAt}
          purchasedTopUpCredits={purchasedTopUpCredits}
          isMobile={isMobile}
          subscriptionPurchase={subscriptionPurchase}
          totalCapturedRolloverCredits={totalCapturedRolloverCredits}
          capturedReferralCreditsCycle={capturedReferralCreditsCycle}
        />
      );
    }

    if (isPackageV3 && !isFreePackage) {
      return (
        <SubscriptionItemV3
          packageInfo={usingOrder?.package}
          usingOrder={usingOrder}
          totalRemainingCredits={totalRemainingCredits}
          purchasedTopUpCredits={purchasedTopUpCredits}
          isMobile={isMobile}
          nextResetAt={nextResetAt}
          subscriptionPurchase={subscriptionPurchase}
          totalCapturedRolloverCredits={totalCapturedRolloverCredits}
          capturedReferralCreditsCycle={capturedReferralCreditsCycle}
        />
      );
    }

    if (isDubbingPackage) return '';

    return (
      <SubscriptionItemV2
        packageInfo={usingOrder?.package}
        usingOrder={usingOrder}
        isMobile={isMobile}
        isFreePackage={isFreePackage}
        isApiPackage={isApiPackage}
        preservationExpiryDate={preservationExpiryDate}
        subscriptionPurchase={subscriptionPurchase}
        lastUsingOrder={lastUsingOrder}
        captureReferralCreditsOneTime={captureReferralCreditsOneTime}
      />
    );
  };

  // Render new UI if feature flag is enabled
  if (isNewUISubscriptionEnabled) {
    return (
      <SubscriptionNew
        usingOrders={usingOrders}
        subscriptionPurchase={subscriptionPurchase}
        totalRemainingCredits={totalRemainingCredits}
        totalRemainingApiCredits={totalRemainingApiCredits}
        totalCapturedRolloverCredits={totalCapturedRolloverCredits}
        capturedReferralCreditsCycle={capturedReferralCreditsCycle}
        remainingCustomCredits={remainingCustomCredits}
        capturedReferralCreditsCustom={capturedReferralCreditsCustom}
        nextResetAt={nextResetAt}
        creditsPerCycle={creditsPerCycle}
        purchasedTopUpCredits={purchasedTopUpCredits}
        lastUsingOrder={lastUsingOrder}
      />
    );
  }

  // Render old UI
  return (
    <StyledSubscriptionV3>
      <div className="header-payment">
        <Typography className="title">{t('myPackages')}</Typography>
      </div>
      <Box className="using-package-list">
        {usingOrders.map((usingOrder) => (
          <Box key={usingOrder.id}>{renderSubscriptionItem(usingOrder)}</Box>
        ))}
      </Box>
    </StyledSubscriptionV3>
  );
};

export default Subscription;
